import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { contractsQueryOptions } from "@/hooks/api/useContracts";
import type { ApiError } from "@/lib/api/core";
import { contractSelectionParams, isEmptySelection, type ContractSelection } from "@/lib/royalty";
import { useOrganizationStore } from "@/stores/organizationStore";
import type { ContractEligibility, ContractStatementPicker } from "@/types";

/** Rows per page in the create-statement contract picker on tall viewports. */
export const CONTRACTS_PAGE_SIZE = 10;
/** Rows per page on short viewports, where a 10-row card overflows the panel. */
export const COMPACT_CONTRACTS_PAGE_SIZE = 6;

interface UseRoyaltyStatementPreviewContractsParams {
  startDate?: Date;
  endDate?: Date;
  /** API filter: which bucket this tab lists (default eligible). */
  eligibility?: ContractEligibility;
  /**
   * With `selection`: partition the eligible bucket by it server-side —
   * true lists the run's contracts, false the ones the user skipped.
   */
  selected?: boolean;
  selection?: ContractSelection;
  /** Already debounced; matches contract name, payees, tracks, releases, ISRCs. */
  search?: string;
  /** DRF ordering param, e.g. "status,-created_at"; default is -created_at. */
  ordering?: string;
  /** 1-based page; the picker paginates instead of accumulating. */
  page?: number;
  /** Rows per page (viewport-dependent at the call site). */
  pageSize?: number;
  enabled?: boolean;
}

/**
 * One page of contracts for bulk statement generation in a period: the
 * statement_picker contract list annotated with period eligibility, split
 * server-side via the `eligibility` and `selected` filters so each tab
 * paginates its own exact list. Header totals come from the preview summary;
 * `count` is this tab's (search-filtered) total.
 */
export function useRoyaltyStatementPreviewContracts({
  startDate,
  endDate,
  eligibility = "eligible",
  selected,
  selection,
  search,
  ordering,
  page = 1,
  pageSize = CONTRACTS_PAGE_SIZE,
  enabled = true,
}: UseRoyaltyStatementPreviewContractsParams) {
  const { currentLabel } = useOrganizationStore();
  const hasPeriod = !!startDate && !!endDate && startDate <= endDate;
  const normalizedSearch = search?.trim() || undefined;

  // Nothing ticked can't be sent: an omitted `contract_ids` means select-all —
  // the opposite. selected=true is then known-empty client-side, and
  // selected=false is simply every eligible contract (no selection params).
  const emptySelection = !!selection && isEmptySelection(selection);
  const knownEmpty = selected === true && emptySelection;
  let selectionFilters: {
    selected?: boolean;
    contractIds?: string[];
    deselectedContractIds?: string[];
  } = {};
  if (selected !== undefined && selection && !emptySelection) {
    const params = contractSelectionParams(selection);
    selectionFilters = {
      selected,
      ...("contract_ids" in params
        ? { contractIds: params.contract_ids }
        : { deselectedContractIds: params.deselected_contract_ids }),
    };
  }

  const { data, isLoading, isError, error } = useQuery({
    ...contractsQueryOptions({
      labelId: currentLabel?.id,
      summary: "statement_picker",
      periodStart: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
      periodEnd: endDate ? format(endDate, "yyyy-MM-dd") : undefined,
      eligibility,
      search: normalizedSearch,
      ordering,
      page,
      pageSize,
      ...selectionFilters,
    }),
    enabled: enabled && hasPeriod && !!currentLabel && !knownEmpty,
    // Typing in search / flipping pages / ticking rows changes the key; keep
    // the last rows on screen mid-fetch.
    placeholderData: keepPreviousData,
  });

  return {
    contracts: knownEmpty ? [] : ((data?.results ?? []) as ContractStatementPicker[]),
    count: knownEmpty ? 0 : (data?.count ?? 0),
    isLoading: !knownEmpty && isLoading,
    isError,
    /** The requested page no longer exists (DRF 404s out-of-range pages). */
    isStalePage: isError && (error as ApiError | null)?.status === 404,
  };
}
